import {
  Backdrop,
  Box,
  CircularProgress,
  Theme,
  useMediaQuery,
} from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { Fragment, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router-dom';

import config from '@/config';
import { Routes, RoutesWithPlayer } from '@/constants';
import {
  Announcements,
  AppErrorBoundary,
  AppModals,
  AppRouteWrapper,
  Player,
} from '@/containers';
import { useIsGrowthbookReady, useListenToPlanChanges } from '@/hooks';
import {
  AnalyticsService,
  initDataDog,
  initGrowthbook,
  initLogRocket,
  initSentry,
} from '@/services';
import {
  selectSessionUserId,
  useAppDispatch,
  useFetchContentPreferencesQuery,
  useFetchPlanQuery,
  useFetchPlayerConfigQuery,
  useFetchPlayerSessionsQuery,
  useFetchPrimaryRegionQuery,
  useFetchRecoveryEmailQuery,
  useFetchUserProfileQuery,
  useFetchUserRegistrationStateQuery,
  useFetchUserTagsQuery,
  useLazyPollForCheckoutSessionCompletionQuery,
  useLinkPlanToUserMutation,
} from '@/store';
import { SnackbarUtils } from '@/utils';

export const AppLoader = () => {
  const dispatch = useAppDispatch();
  const userId = useSelector(selectSessionUserId);

  const {
    data: registrationState,
    isLoading: isUserRegistrationStateLoading,
    error: userRegistrationStateError,
  } = useFetchUserRegistrationStateQuery(userId ? undefined : skipToken);

  const {
    data: profile,
    isLoading: isUserProfileLoading,
    error: fetchUserProfileError,
  } = useFetchUserProfileQuery(userId ? undefined : skipToken);

  const {
    data: plan,
    isLoading: isPlanLoading,
    error: planError,
  } = useFetchPlanQuery(userId ? undefined : skipToken);

  const {
    isLoading: isUserTagsLoading,
    error: userTagsError,
    data: userTags,
  } = useFetchUserTagsQuery(
    userId && registrationState ? undefined : skipToken,
    {
      refetchOnFocus: true,
    },
  );

  const {
    data: playerConfig,
    isLoading: isPlayerConfigLoading,
    error: playerConfigError,
  } = useFetchPlayerConfigQuery(
    userId && registrationState ? undefined : skipToken,
    {
      refetchOnFocus: true,
    },
  );

  useFetchPrimaryRegionQuery(userId ? undefined : skipToken);

  useFetchContentPreferencesQuery(userId ? undefined : skipToken);

  useFetchRecoveryEmailQuery(userId ? undefined : skipToken);

  // TODO discuss dispatching a thunk to deal with all app load data
  // and then using selectors for things like userEpisodes which we know are App loaded
  // and guaranteed to be there
  const { isLoading: isPlayerSessionsLoading, error: playerSessionsError } =
    useFetchPlayerSessionsQuery(!userId ? skipToken : undefined, {
      refetchOnFocus: true,
    });

  const [linkPlanToUser] = useLinkPlanToUserMutation();
  const [pollForCheckoutSessionCompletion] =
    useLazyPollForCheckoutSessionCompletionQuery();

  const { pathname, search } = useLocation();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm'),
  );

  const isGrowthbookReady = useIsGrowthbookReady();

  const isPlayerRoute = useMemo(() => {
    return Object.values(RoutesWithPlayer).some(route =>
      matchPath(route, pathname),
    );
  }, [pathname]);

  const isAppLoading =
    isUserProfileLoading ||
    isPlayerSessionsLoading ||
    isPlanLoading ||
    isUserRegistrationStateLoading ||
    isUserTagsLoading ||
    isPlayerConfigLoading ||
    !isGrowthbookReady;
  const error =
    fetchUserProfileError ||
    planError ||
    playerSessionsError ||
    userRegistrationStateError ||
    userTagsError ||
    playerConfigError;

  const complianceType = playerConfig?.defaultPlayer;

  useEffect(() => {
    initSentry({ userId, profile, complianceType, plan, registrationState });
  }, [userId, profile, complianceType, plan, registrationState]);

  useEffect(() => {
    AnalyticsService.updateUserProfile({
      userProfile: profile,
      complianceType,
      userTags,
      organizationName: plan?.organizationName,
    });
  }, [userId, complianceType, profile, userTags, plan?.organizationName]);

  useEffect(() => {
    initLogRocket({
      userId,
      profile,
      complianceType,
    });
  }, [profile, complianceType, userId]);

  useEffect(() => {
    initDataDog({
      userId,
      profile,
    });
  }, [profile, userId]);

  useEffect(() => {
    initGrowthbook({
      userId: userId!,
      userProfile: profile,
      plan,
      complianceType,
      userTags,
      registrationState,
    });
  }, [plan, profile, complianceType, userId, userTags, registrationState]);

  useEffect(() => {
    const hideIntercom = isMobile || pathname === Routes.CPA_CANADA;
    window.Intercom('boot', {
      user_id: userId,
      email: profile?.email,
      app_id: config.INTERCOM_APP_ID,
      vertical_padding: 100,
      hide_default_launcher: hideIntercom,
    });
  }, [isMobile, pathname, profile?.email, userId]);

  useEffect(() => {
    const linkPlan = async (props: { inviteToken: string }) => {
      try {
        await linkPlanToUser(props).unwrap();
      } catch {
        SnackbarUtils.error('Failed to accept invite. Please contact support.');
        return;
      }

      const url = new URL(window.location.href);
      url.searchParams.delete('inviteToken');
      window.history.replaceState({}, '', url);
    };

    const { inviteToken } = Object.fromEntries(
      new URLSearchParams(search).entries(),
    );

    if (inviteToken && userId) {
      linkPlan({ inviteToken });
    }
  }, [dispatch, linkPlanToUser, userId, search]);

  useListenToPlanChanges();
  useEffect(() => {
    if (pathname === Routes.PURCHASE_SUCCESS) {
      return;
    }
    const waitForCheckoutSessionCompletion = async (
      checkoutSessionId: string,
      isB2C: boolean,
    ) => {
      try {
        await pollForCheckoutSessionCompletion({
          checkoutSessionId,
        }).unwrap();
      } catch {
        SnackbarUtils.error(
          `Failed to create ${isB2C ? 'plan' : 'team'}. Please contact support.`,
        );
      }
    };

    const { checkout_session_id, is_b2c = 'false' } = Object.fromEntries(
      new URLSearchParams(search).entries(),
    );
    if (checkout_session_id) {
      waitForCheckoutSessionCompletion(checkout_session_id, is_b2c === 'true');
    }
  }, [dispatch, search, pathname, pollForCheckoutSessionCompletion]);

  return (
    <Box sx={{ color: 'text.main' }}>
      {error ? (
        <AppErrorBoundary error={error} />
      ) : (
        <Fragment>
          <Backdrop sx={{ zIndex: 'player' }} open={isAppLoading}>
            <CircularProgress />
          </Backdrop>
          <AppRouteWrapper />
          {isPlayerRoute && <Player />}
          <AppModals />
          <Announcements />
        </Fragment>
      )}
    </Box>
  );
};
